import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { CountryISO } from 'ngx-intl-tel-input';
import { SharedService } from '../../../shared/shared.service';
import { API_MODULES } from '../../../core/api-modules';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-contact-oman',
  templateUrl: './contact-oman.component.html',
  styleUrls: ['./contact-oman.component.scss']
})
export class ContactOmanComponent implements OnInit {

  contactForm: FormGroup;

  cities = ['Muscat', 'Sohar', 'Salalah', 'Nizwa', 'Other'];
  selectedCity = 'Muscat';
  selectedEnquiry = 'salesOman@unikai.com';

  enquiryTypes = [
    { label: 'Local Sales', value: 'salesOman@unikai.com' },
    { label: 'Customer Care', value: 'info@unikai.com' }
  ];

  quickAddress = "P.O Box 670, PC 117, Al Kabor, Muscat, Oman";
  quickPhone = "(+968) 24856200";
  whatsappLink = "https://wa.me/96824856200";

  branches = [
    {
      title: "Head Office - Muscat",
      address: "P.O Box 670, PC 117, Al Wadi, Al Kabor, Oman, Muscat",
      phone: "968-24856205",
      timing: "Saturday to Thursday – 07:00 AM to 07:00 PM"
    },
    {
      title: "Branch Office - Barka",
      address: "Opp Al Amri, Barka , Barka, Oman",
      phone: "968-97898576",
      timing: "Saturday to Thursday – 07:00 AM to 07:00 PM"
    },
    {
      title: "Branch Office - Qalat Awamir, Izki",
      address: "Near OmanOil pump, Izki, Oman",
      phone: "968-94238820",
      timing: "Saturday to Thursday – 07:00 AM to 07:00 PM"
    },
    {
      title: "Branch Office - Khaoura",
      address: "Khaoura Industrial Area, North Batina, Oman",
      phone: "968-95584178",
      timing: "Saturday to Thursday – 07:00 AM to 07:00 PM"
    },
    {
      title: "Branch Office - Al Kamil",
      address: "Near Shell Petrol Pump, Al Kamil, Oman",
      phone: "968-978911240",
      timing: "Saturday to Thursday – 07:00 AM to 07:00 PM"
    },
    {
      title: "Branch Office - Salalah",
      address: "Awqad, Plot 206, Near Noor Al Madina Market, Salalah, Oman",
      phone: "968-97891080",
      timing: "Saturday to Thursday – 07:00 AM to 07:00 PM"
    },
    {
      title: "Branch Office - Ibrai",
      address: "Near Ramal Ibrai Hotel, Ibrai, Oman",
      phone: "968-98283857",
      timing: "Saturday to Thursday – 07:00 AM to 07:00 PM"
    }
  ];

  CountryISO = CountryISO;
  /** Global min/max length for national number (same as careers page). */
  PHONE_NATIONAL_MIN = ContactOmanComponent.PHONE_NATIONAL_MIN;
  PHONE_NATIONAL_MAX = ContactOmanComponent.PHONE_NATIONAL_MAX;
  private static readonly PHONE_NATIONAL_MIN = 6;
  private static readonly PHONE_NATIONAL_MAX = 15;

  submitting = false;

  constructor(
    private fb: FormBuilder,
    private api: SharedService
  ) {
    this.contactForm = this.fb.group({
      name: ['', Validators.required],
      mobile: ['', [Validators.required, this.phoneLengthValidator()]],
      email: ['', [Validators.required, Validators.email]],
      city: ['', Validators.required],
      subject: ['', Validators.required],
      enquiryType: ['', Validators.required],
      message: ['']
    });
  }

  ngOnInit(): void {}

  /** Validator: national number length between global min and max (all countries). */
  private phoneLengthValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: unknown } | null => {
      const v = control.value;
      if (!v || typeof v !== 'object' || v.nationalNumber == null) return null;
      const len = String(v.nationalNumber).replace(/\D/g, '').length;
      if (len > 0 &&
        (len < ContactOmanComponent.PHONE_NATIONAL_MIN || len > ContactOmanComponent.PHONE_NATIONAL_MAX)
      ) {
        return {
          phoneLength: {
            min: ContactOmanComponent.PHONE_NATIONAL_MIN,
            max: ContactOmanComponent.PHONE_NATIONAL_MAX,
            actual: len
          }
        };
      }
      return null;
    };
  }

  get defaultPhoneCountry(): CountryISO {
    return CountryISO.Oman;
  }

  get preferredPhoneCountries(): CountryISO[] {
    return [CountryISO.Oman, CountryISO.UnitedArabEmirates];
  }

  private toPhoneString(value: any): string {
    if (!value || typeof value !== 'object') return '';
    return (
      (value.internationalNumber as string) ||
      (value.e164Number as string) ||
      (value.nationalNumber != null ? String(value.nationalNumber) : '')
    );
  }

  private buildPayload(): any {
    const v = this.contactForm.value;
    return {
      country: 'OMAN',
      full_name: (v.name || '').trim(),
      mobile_number: this.toPhoneString(v.mobile),
      email: (v.email || '').trim(),
      emirate: v.city || '',
      subject: v.subject || '',
      enquiry_type: v.enquiryType || '',
      message: v.message || ''
    };
  }

  async onSubmit(): Promise<void> {
    if (!this.contactForm.valid) {
      this.contactForm.markAllAsTouched();
      return;
    }

    const payload = this.buildPayload();
    this.submitting = true;

    try {
      await this.api.create(API_MODULES.CONTACT, payload);

      await Swal.fire({
        icon: 'success',
        title: 'Thank you',
        text: 'Your message has been submitted successfully.',
        confirmButtonColor: '#022a72'
      });

      this.contactForm.reset({
        name: '',
        mobile: '',
        email: '',
        city: '',
        subject: '',
        enquiryType: '',
        message: ''
      });
    } catch (err) {
      await Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'Unable to submit your message. Please try again later.',
        confirmButtonColor: '#022a72'
      });
    } finally {
      this.submitting = false;
    }
  }
}
